/*
 * <product-quiz> — two-question product finder.
 *
 * Every answer's products are already on the page inside <template> tags, so
 * picking an answer is instant and costs no request. Images only load once a
 * template is cloned into the document.
 */
class ProductQuiz extends HTMLElement {
  constructor() {
    super();
    this.state = { option: null, budget: null };
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.hasBudgetStep = Boolean(this.querySelector('[data-quiz-step="2"]'));
    this.resultsEl = this.querySelector('[data-quiz-results]');
    this.resultTitle = this.querySelector('[data-quiz-result-title]');
    this.noteEl = this.querySelector('[data-quiz-note]');
    this.viewAll = this.querySelector('[data-quiz-viewall]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.resultsToShow = parseInt(this.dataset.results, 10) || 4;

    this.addEventListener('click', this.onClick.bind(this));
    this.goTo(1);
  }

  onClick(event) {
    const option = event.target.closest('[data-quiz-option]');
    if (option) return this.pickOption(option);

    const budget = event.target.closest('[data-quiz-budget]');
    if (budget) return this.pickBudget(budget);

    if (event.target.closest('[data-quiz-back]')) return this.goTo(1);
    if (event.target.closest('[data-quiz-restart]')) return this.restart();
  }

  pickOption(button) {
    this.querySelectorAll('[data-quiz-option]').forEach((el) => el.classList.remove('is-selected'));
    button.classList.add('is-selected');

    this.state.option = {
      index: button.dataset.quizOption,
      url: button.dataset.quizUrl,
      name: button.dataset.quizName,
    };

    if (this.hasBudgetStep) {
      this.goTo(2);
    } else {
      this.state.budget = 0;
      this.showResults();
    }
  }

  pickBudget(button) {
    this.querySelectorAll('[data-quiz-budget]').forEach((el) => el.classList.remove('is-selected'));
    button.classList.add('is-selected');
    this.state.budget = parseInt(button.dataset.quizBudget, 10) || 0;
    this.showResults();
  }

  showResults() {
    const pool = this.querySelector(`[data-quiz-pool="${this.state.option.index}"]`);
    if (!pool) return;

    const all = Array.from(pool.content.cloneNode(true).children);
    const max = this.state.budget;
    let matches = max > 0 ? all.filter((card) => parseInt(card.dataset.price, 10) <= max) : all;

    // Never show an empty result — fall back to the cheapest picks instead.
    const relaxed = matches.length === 0;
    if (relaxed) {
      matches = all.sort((a, b) => parseInt(a.dataset.price, 10) - parseInt(b.dataset.price, 10));
    }

    this.resultsEl.replaceChildren(...matches.slice(0, this.resultsToShow));
    if (this.noteEl) this.noteEl.hidden = !relaxed;

    if (this.resultTitle) {
      this.resultTitle.textContent = `Our picks for ${this.state.option.name.toLowerCase()}`;
    }

    if (this.viewAll && this.state.option.url) {
      const suffix = max > 0 ? `?filter.v.price.lte=${Math.round(max / 100)}` : '';
      this.viewAll.href = `${this.state.option.url}${suffix}`;
    }

    this.goTo(3);
  }

  restart() {
    this.state = { option: null, budget: null };
    this.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected'));
    if (this.resultsEl) this.resultsEl.replaceChildren();
    this.goTo(1);
  }

  goTo(step) {
    this.steps.forEach((el) => {
      el.hidden = Number(el.dataset.quizStep) !== step;
    });

    if (this.progress) {
      const total = this.hasBudgetStep ? 3 : 2;
      const done = this.hasBudgetStep ? step : Math.min(step, 2);
      this.progress.style.setProperty('--quiz-progress', `${(done / total) * 100}%`);
    }

    if (step > 1) {
      const target = this.querySelector(`[data-quiz-step="${step}"]`);
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }
}

if (!customElements.get('product-quiz')) {
  customElements.define('product-quiz', ProductQuiz);
}
